'use client';
import { Lists } from '.generated/Feature.CW.model';
import { Field, ImageField, Text } from '@sitecore-content-sdk/nextjs';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { JSX } from 'react';

import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import ThemeIconWrapper from 'helpers/ThemeIconWrappers/ThemeIconWrapper';
import { ComponentProps } from 'lib/component-props';
import useIsEditing from 'lib/hooks/useIsEditing';
import { useState } from 'react';
import { tv } from 'tailwind-variants';

export type CardItemsProps = Lists.NavigationCardItem &
  ComponentProps & {
    varient: string;
    fields?: {
      cardIcon?: {
        fields?: {
          Value?: Field<string>;
          Icon?: ImageField;
        };
      };
    };
  };

const NavigationCardItem = (props: CardItemsProps): JSX.Element => {
  const {
    expandedCardWrapper,
    imageContainer,
    imageClass,
    iconContainer,
    expandedCardTitle,
    cardDescription,
    cardArrow,
    condensedCardWrapper,
    condensedCardTitle,
  } = TailwindVariants();

  const [isHovered, setIsHovered] = useState(false);

  const isEditingData = useIsEditing();

  const animatedArrowIcon = (isCondensed: boolean) => {
    return (
      <>
        {isCondensed && (
          <div
            className={`${cardArrow()} overflow-hidden duration-700 relative justify-self-end ${isHovered ? 'bg-blue-base' : ''} md:absolute md:bottom-5 md:right-6`}
          >
            <SvgIcon
              icon="arrow-right"
              size="sm"
              className={`duration-700 ease-in-out absolute text-blue-base ${isHovered ? 'left-[60%]' : 'left-[30%]'}`}
            />
            <SvgIcon
              icon="arrow-right"
              size="sm"
              className={`duration-700 ease-in-out absolute text-white ${isHovered ? 'left-[30%]' : '-left-[30%]'}`}
            />
          </div>
        )}
      </>
    );
  };

  const expandedVersion = () => {
    const customFields = {
      Value: props?.fields?.cardIcon?.fields?.Value,
      Icon: props?.fields?.cardIcon?.fields?.Icon,
    };
    return (
      <>
        {props?.fields?.cardImage?.value?.src ? (
          <div className={imageContainer()}>
            <ImageWrapper field={props?.fields?.cardImage} className={imageClass()} />
          </div>
        ) : (
          <div className={iconContainer()}>
            <ThemeIconWrapper
              iconClass="!h-[41px] !w-[41px] text-blue-700"
              fields={customFields ?? {}}
              imageClass={imageClass()}
            />
          </div>
        )}

        <div
          className={`${expandedCardTitle()} ${props?.fields?.cardImage?.value?.src !== '' ? 'md:pt-12 pt-9' : 'mt-10'}`}
        >
          <Text field={props?.fields?.heading} />
        </div>
        <div
          className={`${cardDescription()} ${props?.fields?.cardImage?.value?.src !== '' ? 'mb-2' : 'mb-6'}`}
        >
          <Text field={props?.fields?.description} />
        </div>

        {props?.fields?.cardLink?.value?.href && animatedArrowIcon(false)}
      </>
    );
  };

  const condensedVersion = () => {
    return (
      <>
        <div className={condensedCardTitle()}>
          <Text field={props?.fields?.heading} />
        </div>
        {props?.fields?.cardLink?.value?.href && animatedArrowIcon(true)}
      </>
    );
  };

  if (props?.varient?.toLowerCase() === 'expandedversion') {
    return (
      <>
        {isEditingData ? (
          <div
            className={` ${expandedCardWrapper()} ${props?.fields?.cardImage?.value?.src !== '' ? '' : 'md:pt-12 pt-9'} ${isHovered ? 'shadow-hover' : 'shadow-default'}`}
          >
            <EditingHelpText>Edit card link:</EditingHelpText>
            <LinkWrapper field={props?.fields?.cardLink} />
            {expandedVersion()}
          </div>
        ) : (
          props?.fields?.heading?.value &&
          (props?.fields?.cardLink?.value?.href ? (
            <LinkWrapper
              field={props?.fields?.cardLink}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={` ${expandedCardWrapper()} ${props?.fields?.cardImage?.value?.src !== '' ? '' : 'md:pt-12 pt-9'} ${isHovered ? 'shadow-hover' : 'shadow-default'}`}
            >
              {expandedVersion()}
            </LinkWrapper>
          ) : (
            <div
              className={` ${expandedCardWrapper()} ${props?.fields?.cardImage?.value?.src !== '' ? '' : 'md:pt-12 pt-9'} ${isHovered ? 'shadow-hover' : 'shadow-default'}`}
            >
              {expandedVersion()}
            </div>
          ))
        )}
      </>
    );
  } else {
    return (
      <>
        {isEditingData ? (
          <div
            className={` ${condensedCardWrapper()} ${isHovered ? 'shadow-hover' : 'shadow-default'} !block`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <EditingHelpText>Edit card link:</EditingHelpText>
            <LinkWrapper field={props?.fields?.cardLink} />
            {condensedVersion()}
          </div>
        ) : (
          props?.fields?.heading?.value &&
          (props?.fields?.cardLink?.value?.href ? (
            <LinkWrapper
              field={props?.fields?.cardLink}
              className={` ${condensedCardWrapper()} ${isHovered ? 'shadow-hover' : 'shadow-default'}`}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {condensedVersion()}
            </LinkWrapper>
          ) : (
            <div
              className={`${condensedCardWrapper()} ${isHovered ? 'shadow-hover' : 'shadow-default'}`}
            >
              {condensedVersion()}
            </div>
          ))
        )}
      </>
    );
  }
};

export default NavigationCardItem;

const TailwindVariants = tv({
  slots: {
    expandedCardWrapper:
      'cardItem block md:px-12 md:pb-12 px-9 pb-7 bg-white rounded-md !w-full !h-auto flex flex-col',
    condensedCardWrapper:
      'cardItem relative flex items-center justify-between gap-[12px] md:px-12 md:pb-[58px] md:pt-9 px-9 py-7 bg-white rounded-md !w-full !h-auto',
    imageContainer: '-mx-9 md:-mx-12',
    imageClass: 'w-full rounded-t-md ',
    iconContainer:
      'w-[70px] h-[70px] bg-blue-50 rounded-full flex items-center justify-center mt-[24px]',
    expandedCardTitle:
      'mb-8 font-centuryGothic font-bold md:text-22 text-20 md:leading-31 leading-28 text-gray-900',
    condensedCardTitle:
      'flex-1 font-centuryGothic font-bold md:text-22 text-20 md:leading-31 leading-28 text-gray-900',
    cardDescription: 'text-16 leading-25 text-gray-900 font-nunitoSans font-[500]',
    cardArrow:
      'md:w-[48px] md:h-[48px] w-[40px] h-[40px] rounded-full border-2 text-center border-blue-base flex items-center justify-center transition-all duration-300 ease-in-out',
  },
});
